import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { assertCondition, assertNotNil, isNotNil } from '../../utils/types';
import { useImmer } from 'use-immer';
import { MaybeNil } from '../../utils/interfaces';
import { DND_GRID_GAP } from './constants';
import { isNil } from 'lodash';
import { IColSpanUpdater, IRowMinHeightUpdater } from './types';
import { useResizeDetector } from 'react-resize-detector';
import { useDndGridInternalContext } from './context';
import isEqual from 'lodash/isEqual';
import { DASHBOARD_COLUMNS_PER_ROW } from '../../pages/DashboardRoute/utils/constants';

interface IPrivateState {
  rowHeight: MaybeNil<number>;
  rowWidth: MaybeNil<number>;
  rowIndex: MaybeNil<number>;
  colSpans: MaybeNil<number>[];
  minColumnWidth: MaybeNil<number>;
}

// gaps between columns are one less than the number of rows (1 | 2 | 3)
const GAPS_COUNT = DASHBOARD_COLUMNS_PER_ROW - 1;

const createConverter = (rowPixelWidth: number) => {
  const oneColSpanPixelWidth =
    (rowPixelWidth - GAPS_COUNT * DND_GRID_GAP) / DASHBOARD_COLUMNS_PER_ROW;

  // precalculate column breakpoint sizes
  const colSpansWidthInPixels: number[] = [oneColSpanPixelWidth];
  for (let i = 1; i < DASHBOARD_COLUMNS_PER_ROW; i++) {
    colSpansWidthInPixels[i] =
      colSpansWidthInPixels[i - 1] + DND_GRID_GAP + oneColSpanPixelWidth;
  }

  return {
    toPixelWidth: (colSpan: number) =>
      oneColSpanPixelWidth * colSpan + (colSpan - 1) * DND_GRID_GAP,
    toColSpan: (columnPixelWidth: number) => {
      // find the nearest colspan based on the columnPixelWidth
      let closestColSpan: number | null = null;
      let smallestDelta: number | null = null;
      for (let i = 0; i < colSpansWidthInPixels.length; i++) {
        const delta = Math.abs(colSpansWidthInPixels[i] - columnPixelWidth);
        if (smallestDelta === null || delta < smallestDelta) {
          smallestDelta = delta;
          closestColSpan = i;
        } else {
          // no need to search anymore because breakpoints array is sorted
          // thus always increasing
          break;
        }
      }
      assertNotNil(closestColSpan);
      return closestColSpan + 1; // zero-indexed breakpoint to one-indexed colSpan
    },
  };
};

export const useCreateRowColumnResizerContext = (
  rowHeight: MaybeNil<number>,
  onUpdateColSpan: IColSpanUpdater,
  onUpdateRowMinHeight: IRowMinHeightUpdater,
) => {
  const { disabled } = useDndGridInternalContext();

  const privateStateRef = useRef<IPrivateState>({
    colSpans: [],
    minColumnWidth: null,
    rowWidth: null,
    rowIndex: null,
    rowHeight: null,
  });

  const [columnsWidthInPixels, setColumnsWidthInPixels] = useImmer<
    MaybeNil<number>[]
  >([]);

  const [rowHeightInPixels, setRowHeightInPixels] =
    useState<MaybeNil<number>>(rowHeight);

  const recalculateColumnsPixelWidth = useCallback(() => {
    const { colSpans, rowWidth } = privateStateRef.current;
    if (isNil(rowWidth)) {
      return;
    }
    const { toPixelWidth } = createConverter(rowWidth);
    privateStateRef.current.minColumnWidth = toPixelWidth(2);
    setColumnsWidthInPixels((draft) => {
      for (let i = 0; i < colSpans.length; i++) {
        const colSpan = colSpans[i];
        if (!isNil(colSpan)) {
          draft[i] = toPixelWidth(colSpan);
        } else {
          draft[i] = null;
        }
      }
    });
  }, [setColumnsWidthInPixels]);

  const { ref: rowRefCallback } = useResizeDetector<HTMLElement>({
    onResize: (width, height) => {
      if (isNil(height) || isNil(width)) {
        return;
      }

      // we support only whole numbers for height, round if fraction
      if (privateStateRef.current.rowHeight !== Math.round(height)) {
        privateStateRef.current.rowHeight = Math.round(height);
      }

      if (privateStateRef.current.rowWidth !== width) {
        privateStateRef.current.rowWidth = width;
        recalculateColumnsPixelWidth();
      }
    },
  });

  const registerColumn = useCallback(
    (rowIndex: number, colIndex: number, colSpan: MaybeNil<number>) => {
      privateStateRef.current.rowIndex = rowIndex;
      privateStateRef.current.colSpans[colIndex] = colSpan;
      recalculateColumnsPixelWidth();
      return () => {
        privateStateRef.current.colSpans[colIndex] = null;
        recalculateColumnsPixelWidth();
      };
    },
    [recalculateColumnsPixelWidth],
  );

  const resizeColumnProgressHandler = useCallback(
    (e: MouseEvent, rightColIndex: number) => {
      assertCondition(rightColIndex > 0);
      const { minColumnWidth } = privateStateRef.current;
      assertNotNil(minColumnWidth);
      setColumnsWidthInPixels((draft) => {
        const leftColumn = draft[rightColIndex - 1];
        const rightColumn = draft[rightColIndex];
        assertNotNil(leftColumn);
        assertNotNil(rightColumn);

        const delta = e.movementX;
        const newLeftColumnWidth = leftColumn + delta;
        const newRightColumnWidth = rightColumn - delta;
        if (
          newLeftColumnWidth < minColumnWidth ||
          newRightColumnWidth < minColumnWidth
        ) {
          return;
        }
        draft[rightColIndex - 1] = newLeftColumnWidth;
        draft[rightColIndex] = newRightColumnWidth;
      });
    },
    [setColumnsWidthInPixels],
  );

  const resizeColumnEndHandler = useCallback(() => {
    const { rowWidth, rowIndex } = privateStateRef.current;
    assertNotNil(rowIndex);
    assertNotNil(rowWidth);

    const { toColSpan } = createConverter(rowWidth);
    // unregistered (moved) columns are marked as null, and we filter them out because they are ignored when calculating
    // newColSpans and will trigger isChanged checked below because of deep comparison
    const oldColSpans = privateStateRef.current.colSpans.filter(isNotNil);
    const newColSpans = columnsWidthInPixels.filter(isNotNil).map(toColSpan);
    // detect if the colSpan has changed before assigning the new value to the state so we can trigger onUpdateColSpan
    // callback once all state is updated
    const isChanged = !isEqual(newColSpans, oldColSpans);
    privateStateRef.current.colSpans = newColSpans;

    // although colSpans may not be changed, we still need to recalculate the pixel width because it may have changed
    // due to the resizing from the user. Visually, it snaps back to the colspan grid
    recalculateColumnsPixelWidth();

    if (isChanged) {
      onUpdateColSpan(rowIndex, newColSpans);
    }
  }, [columnsWidthInPixels, recalculateColumnsPixelWidth, onUpdateColSpan]);

  const canResizeHorizontally = useCallback(
    (colIndex: number) => {
      return (
        !disabled &&
        colIndex > 0 &&
        !isNil(columnsWidthInPixels[colIndex - 1]) &&
        !isNil(columnsWidthInPixels[colIndex])
      );
    },

    [columnsWidthInPixels, disabled],
  );

  const resizeRowStartHandler = useCallback(() => {
    assertNotNil(privateStateRef.current.rowHeight);
    setRowHeightInPixels(privateStateRef.current.rowHeight);
  }, []);

  const resizeRowProgressHandler = useCallback((e: MouseEvent) => {
    setRowHeightInPixels((prevState) => {
      assertNotNil(prevState);
      const delta = e.movementY;
      return prevState + delta;
    });
  }, []);

  const resizeRowEndHandler = useCallback(
    (_: MouseEvent, bottomRowIndex: number) => {
      assertNotNil(privateStateRef.current.rowHeight);
      if (rowHeight !== privateStateRef.current.rowHeight) {
        onUpdateRowMinHeight(
          bottomRowIndex - 1,
          privateStateRef.current.rowHeight,
        );
      }
    },
    [onUpdateRowMinHeight, rowHeight],
  );

  const canResizeVertically = useCallback(
    (rowIndex: number) => !disabled && rowIndex > 0,
    [disabled],
  );

  return useMemo(
    () => ({
      canResizeHorizontally,
      canResizeVertically,
      columnsWidthInPixels,
      registerColumn,
      resizeColumnEndHandler,
      resizeColumnProgressHandler,
      resizeRowEndHandler,
      resizeRowProgressHandler,
      resizeRowStartHandler,
      rowHeightInPixels,
      rowRefCallback,
    }),
    [
      canResizeHorizontally,
      canResizeVertically,
      columnsWidthInPixels,
      registerColumn,
      resizeColumnEndHandler,
      resizeColumnProgressHandler,
      resizeRowEndHandler,
      resizeRowProgressHandler,
      resizeRowStartHandler,
      rowHeightInPixels,
      rowRefCallback,
    ],
  );
};

type IRowColumnResizerContext = ReturnType<
  typeof useCreateRowColumnResizerContext
>;

export const DndGridRowColumnResizerContext =
  createContext<IRowColumnResizerContext | null>(null);

export const useRowColumnResizerContext = () => {
  const context = useContext(DndGridRowColumnResizerContext);
  assertNotNil(context);
  return context;
};
